import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Eye, Package, Image, ClipboardList, Search, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PublicLazyImage({ token, modelName, hasImage }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasImage) return;
    setLoading(true);
    axios.get(`${API}/viewfinder/${token}/image/${encodeURIComponent(modelName)}`)
      .then(r => setSrc(r.data?.image_url || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, modelName, hasImage]);

  return (
    <div className="aspect-square bg-slate-100 flex items-center justify-center">
      {loading ? <div className="animate-pulse w-full h-full bg-slate-200" /> :
       src ? <img src={src} alt={modelName} className="w-full h-full object-cover" /> :
       <div className="text-center"><Image className="w-10 h-10 text-slate-300 mx-auto" /><p className="text-[10px] text-slate-400 mt-1">No image</p></div>}
    </div>
  );
}

export default function ViewfinderPublic() {
  const { token } = useParams();
  const [items, setItems] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("inventory");
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/viewfinder/${token}`).catch(e => { setError(e.response?.status === 403 ? "Invalid or expired link" : "Failed to load"); return null; }),
      axios.get(`${API}/viewfinder/${token}/attributes`).catch(() => ({ data: { attributes: [] } })),
    ])
      .then(([inv, attr]) => {
        if (inv) { setItems(inv.data?.items || []); }
        setAttributes(attr?.data?.attributes || []);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = items.filter(i => i.model_name?.toLowerCase().includes(search.toLowerCase()));

  // Merge for attributes view
  const attrMap = {};
  attributes.forEach(a => { attrMap[a.product?.toUpperCase()] = a; });
  const mergedAttrs = filtered.map(item => {
    const a = attrMap[item.model_name?.toUpperCase()] || {};
    return { ...item, ...a, product: item.model_name };
  });

  const handleDownload = async () => {
    try {
      const r = await axios.get(`${API}/viewfinder/${token}/attributes/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "product_attributes.xlsx"; a.click();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><Eye className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="text-xl font-semibold text-slate-700">{error}</p></div></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="viewfinder-public">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-800 rounded-sm flex items-center justify-center"><Eye className="w-5 h-5 text-white" /></div>
            <div><h1 className="font-semibold text-lg text-slate-900">AP Product Catalog</h1><p className="text-xs text-slate-500">{items.length} products</p></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
              {[{ id: "inventory", label: "Catalog", icon: Package }, { id: "attributes", label: "Attributes", icon: ClipboardList }].map(t => (
                <button key={t.id} onClick={() => setView(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                  <t.icon className="w-3.5 h-3.5" />{t.label}
                </button>
              ))}
            </div>
            {view === "attributes" && (
              <button onClick={handleDownload} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-800 text-white hover:bg-slate-700 transition-colors" data-testid="public-download-attrs">
                <Download className="w-3.5 h-3.5" />Download
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
        </div>

        {view === "inventory" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map(item => (
              <div key={item.sr_no} className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
                <PublicLazyImage token={token} modelName={item.model_name} hasImage={item.has_image} />
                <div className="p-3">
                  <h3 className="font-medium text-sm text-slate-800 truncate">{item.model_name}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-base font-bold text-slate-900">Rs.{item.mrp?.toLocaleString()}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.quantity > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {item.quantity > 0 ? `${item.quantity} pcs` : "Out"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {view === "attributes" && (
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm" data-testid="public-attributes-table">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="px-3 py-2.5 text-left">Product</th>
                  <th className="px-3 py-2.5 text-right">Stock</th>
                  <th className="px-3 py-2.5 text-right">MRP</th>
                  <th className="px-3 py-2.5 text-left">Case Size</th>
                  <th className="px-3 py-2.5 text-left">Case Material</th>
                  <th className="px-3 py-2.5 text-left">Strap Material</th>
                  <th className="px-3 py-2.5 text-left">Deployment</th>
                  <th className="px-3 py-2.5 text-left">Crystal</th>
                  <th className="px-3 py-2.5 text-left">Water Resist.</th>
                  <th className="px-3 py-2.5 text-left">Movement</th>
                  <th className="px-3 py-2.5 text-left">Power Reserve</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {mergedAttrs.map((item, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium">{item.product}</td>
                    <td className="px-3 py-2.5 text-right"><span className={item.quantity === 0 ? "text-red-600 font-bold" : ""}>{item.quantity}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono">Rs.{item.mrp?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.case_size || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.case_material || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.strap_material || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.deployment_type || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.crystal || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.water_resistance || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.movement || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.power_reserve || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
